'use strict';

const libPictView = require('pict-view');

/**
 * ProjectEditWorkspace — the bespoke Project EDIT screen (reached by the app-router Edit bridge on
 * #/PSRS/Project/Edit/<GUID>, which shadows the generic recordset edit form). A thin shell: all data,
 * behavior, and hand-built form HTML live in the ProjectEditWorkspace provider, which assigns into the
 * #PEW-Body mount (Entitlements pattern). It exists so the Financial & Schedule fields can be locked once
 * a project is Active — something the manifest-driven recordset form can't express per-record.
 */
class ProjectEditWorkspace extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get provider() { return this.pict.providers.ProjectEditWorkspace; }

	onAfterRender(pRenderable)
	{
		this.pict.CSSMap.injectCSS();
		const tmpProvider = this.provider;
		if (tmpProvider)
		{
			tmpProvider.render();
			// Repaint the CustomProperties mount too (it's a sibling of #PEW-Body); no-op until loaded.
			tmpProvider.renderCustomProps();
		}
		return super.onAfterRender(pRenderable);
	}

	/**
	 * Lazily register an EDITABLE pict-section-objecteditor over the project's CustomProperties, bound to
	 * AppData.ProjectEditWorkspace.CustomPropsData (mutated in place as the user edits). Mirrors ProjectWorkspace's
	 * read-only tree, but Editable + into this screen's own mount so a body repaint never destroys it.
	 */
	_ensureCustomPropsView()
	{
		const tmpHash = 'PEW-CustomPropsEditor';
		if (this.pict.views[tmpHash]) { return this.pict.views[tmpHash]; }
		const libObjectEditor = require('pict-section-objecteditor');
		this.pict.addView(tmpHash, Object.assign({}, libObjectEditor.default_configuration,
			{
				ViewIdentifier: tmpHash,
				AutoRender: false,
				Editable: true,
				InitialExpandDepth: 2,
				ObjectDataAddress: 'AppData.ProjectEditWorkspace.CustomPropsData',
				Renderables:
				[
					{
						RenderableHash: 'ObjectEditor-Container',
						TemplateHash: 'ObjectEditor-Container-Template',
						DestinationAddress: '#PEW-CustomPropsHost',
						RenderMethod: 'replace',
					}
				],
			}), libObjectEditor);
		const tmpView = this.pict.views[tmpHash];
		// The object editor builds its per-type node renderers in onBeforeInitialize; a view added after the
		// boot cycle isn't initialized automatically, so initialize it before the first render.
		if (tmpView && (typeof tmpView.initialize === 'function')) { tmpView.initialize(); }
		return tmpView;
	}

	/** Render the editable CustomProperties tree into #PEW-CustomPropsHost (called by the provider). */
	mountCustomProps()
	{
		const tmpView = this._ensureCustomPropsView();
		if (!tmpView) { return; }
		tmpView.options.DefaultDestinationAddress = '#PEW-CustomPropsHost';
		tmpView.render();
	}
}

module.exports = ProjectEditWorkspace;

module.exports.default_configuration =
{
	ViewIdentifier: 'ProjectEditWorkspace',
	DefaultRenderable: 'ProjectEditWorkspaceWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		.pew-wrap { padding: 1.25rem 1.5rem 2rem; }
		.pew-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin: 0 0 1rem; }
		.pew-head-actions { display: flex; gap: 0.5rem; flex: 0 0 auto; }
		.pew-eyebrow { font-size: 0.72rem; font-weight: 650; text-transform: uppercase; letter-spacing: 0.06em; color: var(--theme-color-text-muted, #6b7686); }
		.pew-title { font-size: 1.5rem; font-weight: 700; line-height: 1.2; margin: 0.1rem 0 0; color: var(--theme-color-text-primary, #1f2733); }
		.pew-lock-banner { display: flex; align-items: center; gap: 0.55rem; font-size: 0.9rem; line-height: 1.4; color: var(--theme-color-text-secondary, #45505f);
			background: var(--theme-color-status-warning-bg, #fdf6e3); border: 1px solid var(--theme-color-status-warning, #d9a441); border-radius: 8px; padding: 0.6rem 0.85rem; margin: 0 0 1rem; }
		.pew-lock-icon { flex: 0 0 auto; font-size: 1rem; }
		.pew-form { border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 8px; background: var(--theme-color-background-panel, #fff); padding: 1.4rem 1.5rem; }
		.pew-group { margin: 0 0 1.4rem; }
		.pew-group:last-child { margin-bottom: 0; }
		.pew-group-head { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--theme-color-brand-primary, #5650e6);
			background: var(--theme-color-background-selected, #eef1f8); padding: 0.35rem 0.7rem; border-radius: 6px; margin: 0 0 0.9rem; display: flex; align-items: center; }
		.pew-group-hint { margin-left: 0.6rem; font-size: 0.66rem; font-weight: 700; letter-spacing: 0.03em; color: var(--theme-color-status-warning, #b8860b); text-transform: none; }
		.pew-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr)); gap: 0.9rem 1.4rem; }
		.pew-field.pew-field-wide { grid-column: 1 / -1; }
		.pew-field-label { display: block; font-size: 0.68rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-muted, #6b7686); margin-bottom: 0.25rem; }
		.pew-input { width: 100%; }
		.pew-input-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.82rem; }
		.pew-field-error { color: var(--theme-color-status-danger, #c0392b); font-size: 0.78rem; margin-top: 0.22rem; }
		.pew-input[disabled], .pew-input select[disabled] { background: var(--theme-color-background-tertiary, #f4f6f9); color: var(--theme-color-text-muted, #8894a4); cursor: not-allowed; }
		.pew-picker-host { width: 100%; }
		.pew-customprops-panel { margin-top: 1.1rem; }
		.pew-customprops-help { font-size: 0.8rem; color: var(--theme-color-text-muted, #6b7686); margin: 0 0 0.6rem; }
		.pew-objecteditor-host { border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; padding: 0.6rem 0.8rem; background: var(--theme-color-background-tertiary, #f9fafb); min-height: 3rem; overflow: auto; }
		.pew-empty { padding: 2.5rem 1rem; }
		.pew-spinner { display: inline-block; width: 1.6rem; height: 1.6rem; border: 3px solid var(--theme-color-border-default, #dbdbdb); border-top-color: var(--theme-color-brand-primary, #5650e6); border-radius: 50%; animation: pew-spin 0.8s linear infinite; }
		@keyframes pew-spin { to { transform: rotate(360deg); } }
	`,
	Templates:
	[
		{
			Hash: 'ProjectEditWorkspace-Wrap',
			Template: /*html*/`<div class="pew-wrap"><div id="PEW-Body"></div><div id="PEW-CustomProps"></div></div>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'ProjectEditWorkspaceWrap',
			TemplateHash: 'ProjectEditWorkspace-Wrap',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
			RenderMethod: 'replace',
		},
	],
};
